import classNames from "classnames";
import React from "react";
import { Link } from "react-router-dom";
import "./Button.scss";
import { Svg } from "../../Svg/Svg";
import { Typography } from "../../Typography/Typography";
import LoadingGif from "../../../../assets/images/gif/sdap-loading.json";
import Lottie from "lottie-react";
interface Props {
  title: string;
  type?: "primary" | "secondary" | "content";
  variant?: "solid" | "outlined";
  tooltip?: string;
  disabled?: boolean;
  icon?: string;
  iconSize?: number;
  extraIcon?: string;
  iconPosition?: "left" | "right";
  className?: string;
  onClick?: () => void; // Reaquired in implementation
  id?: string;
  isLoading?: boolean;
  testId?: string;
  tabIndex?: number;
}

export const Button: React.FC<Props> = (props) => {
  const {
    tooltip,
    title,
    type = "primary",
    disabled,
    onClick,
    variant = "solid",
    icon,
    iconSize = 16,
    iconPosition = "right",
    className = "",
    id,
    extraIcon,
    testId,
    tabIndex,
  } = props;
  const wrapClass = classNames("button", {
    [`button--${type}`]: !!type,
    [`button--${variant}`]: !!variant,
    [`button--${type}--icon`]: !!icon,
    "disabled-button": disabled,
    [className]: !!className,
  });

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      {icon && iconPosition === "left" ? (
        <button
          title={tooltip}
          className={wrapClass}
          disabled={disabled}
          onClick={handleClick}
          id={id}
          data-testid={testId}
          tabIndex={tabIndex}
        >
          <span>
            {icon && (
              <Svg
                icon={icon}
                width={iconSize}
                height={iconSize}
                className="left-icon"
                title={title}
              ></Svg>
            )}
            {title}
            {extraIcon && (
              <Svg
                icon={extraIcon}
                width={iconSize}
                height={iconSize}
                className="extra-icon-right"
                title={title}
              ></Svg>
            )}
          </span>
        </button>
      ) : (
        <button
          title={tooltip}
          className={wrapClass}
          disabled={disabled}
          onClick={handleClick}
          id={id}
          data-testid={testId}
          tabIndex={tabIndex}
        >
          <span>
            {extraIcon && (
              <Svg
                icon={extraIcon}
                width={iconSize}
                height={iconSize}
                className="extra-icon-left"
                title={title}
              ></Svg>
            )}
            {title}
            {icon && (
              <Svg
                icon={icon}
                width={iconSize}
                height={iconSize}
                className="right-icon"
                title={title}
              ></Svg>
            )}
          </span>
        </button>
      )}
    </>
  );
};

export enum LinkTarget {
  BLANK = "_blank",
  SELF = "_self",
  PARENT = "_parent",
  TOP = "_top",
}

interface LinkProps {
  to: string | {};
  title: string;
  underlined?: boolean;
  target?: LinkTarget;
  icon?: string;
  iconSize?: number;
  iconPosition?: "left" | "right";
  size?: "sm" | "lg";
  className?: string;
  textClassName?: string;
  onClick?: (e?: any) => void;
  testId?: string;
  toolTip?: string;
  id?: string;
  svgIconToolTip?: string;
  tabIndex?: number;
}
export const LinkButton: React.FC<LinkProps> = (props) => {
  const {
    to,
    title,
    underlined = true,
    target = LinkTarget.SELF,
    icon,
    iconSize = 16,
    iconPosition = "right",
    className = "",
    size = "sm",
    textClassName = "",
    onClick,
    testId,
    toolTip,
    svgIconToolTip,
    id,
    tabIndex,
  } = props;

  const wrapClass = classNames("link link--text", {
    [`link--text--underlined`]: !!underlined,
    [`link--text--${size}`]: !!size,
    [`link--text--${size}--icon`]: !!icon,
    [className]: !!className,
  });
  return (
    <>
      <Link
        tabIndex={tabIndex}
        to={to}
        className={wrapClass}
        target={target}
        onClick={(e) => {
          if (onClick) onClick(e);
        }}
        data-testid={testId}
        id={id}
        title={toolTip}
      >
        {icon && iconPosition !== "left" ? (
          <Typography variant="body2" component="span">
            <span className={textClassName}>
              {title}
              {icon && (
                <Svg
                  icon={icon}
                  width={iconSize}
                  height={iconSize}
                  className="right-icon"
                ></Svg>
              )}
            </span>
          </Typography>
        ) : (
          <Typography
            variant="body2"
            component="span"
            className={textClassName}
          >
            <span>
              {icon && (
                <Svg
                  icon={icon}
                  width={iconSize}
                  height={iconSize}
                  className="left-icon"
                  title={svgIconToolTip}
                ></Svg>
              )}
              {title}
            </span>
          </Typography>
        )}
      </Link>
    </>
  );
};

interface LoadingButtonProps {
  title: string;
  type?: "primary" | "secondary" | "content";
  variant?: "solid" | "outlined";
  tooltip?: string;
  disabled?: boolean;
  loadingPosition?: "left" | "right";
  className?: string;
  onClick?: () => void;
  id?: string;
  isLoading?: boolean;
  testId?: string;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  tooltip,
  title,
  type = "primary",
  disabled,
  onClick,
  variant = "solid",
  loadingPosition = "right",
  className = "",
  id,
  isLoading,
  testId,
}) => {
  const wrapClass = classNames("button", {
    [`button--${type}`]: !!type,
    [`button--${variant}`]: !!variant,
    "disabled-button": disabled,
    [className]: !!className,
  });

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <button
      title={tooltip}
      className={wrapClass}
      disabled={disabled}
      onClick={handleClick}
      id={id}
      data-testid={testId}
    >
      <span>
        <div className={"button-text-icon-container"}>
          {loadingPosition === "left" && isLoading && (
            <Lottie
              className="loading-button__spinner_left"
              animationData={LoadingGif}
              loop={true}
              style={{ width: 20, height: 20 }}
            />
          )}
          <span>{title}</span>
          {loadingPosition === "right" && isLoading && (
            <Lottie
              className="loading-button__spinner_right"
              animationData={LoadingGif}
              loop={true}
              style={{ width: 20, height: 20 }}
            />
          )}
        </div>
      </span>
    </button>
  );
};
